import { useEffect, useState } from 'react'

import { api, type PendingProposal } from '../api/client'

/**
 * useAwaitingProposal — is a change to THIS workflow already proposed and waiting?
 *
 * REPORTED BY THE FLEET OWNER, 2026-09-17, with a screenshot: "I have closed the PR of the
 * last run … I did so via the improvement screen and github. But UI of
 * …/daily-check-report should really now reflect this."
 *
 * The panel was right when it was drawn and never drawn again. Its effect lived inline in
 * Workflows.tsx with a dependency array of `[selected]`, so it read once per workflow and
 * never afterwards — an operator who declined the change, or closed the pull request on
 * GitHub, kept being told it was pending until the facet remounted. And because the Propose
 * button is disabled behind that claim, the surface blocked the one action they had just
 * made possible.
 *
 * THE SAME DEFECT THIS PROGRAMME KEEPS FINDING, one layer further out: a fact read once and
 * reported as current. The Results card asserted a measurement that never ran; the rail read
 * an absent slot as an absent run; this read a closed pull request as an open one. Each time
 * the data was honest and the READING was stale.
 *
 * EXTRACTED SO IT CAN BE DRIVEN. Inside a six-thousand-line facet this decision had no test
 * and no way to get one; a hook can be rendered by a probe component in milliseconds, which
 * is why the refresh below has five cases against it rather than a comment promising it
 * works.
 *
 * ---------------------------------------------------------------------------
 * AND THEN IT WAS STILL WRONG, reported by the same fleet owner the NEXT DAY on athena's
 * ticket-triage/triage: they had denied the change and closed PR #646 four days earlier and
 * the editor still refused to let them propose, pointing at a decision they had made. The
 * refresh above was working perfectly. It was re-reading a question whose ANSWER could not
 * express what they had done.
 *
 * Making the read fresh was never enough, because this hook was deriving a yes/no from the
 * improvements INDEX, and that list cannot answer it. Three reasons, each sufficient on its
 * own to wedge a workflow for good:
 *
 *   - `?status=proposed` DOES NOT MEAN the rows are proposed. The filter is applied upstream
 *     against the stored column and then live resolution rewrites `status` in place, so the
 *     response legitimately contains rows reading `deployed`. On athena the day this was
 *     reported: 153 rows returned, 24 of them not proposed. Six workflows were blocked by a
 *     change that had already shipped, under a banner saying "waiting on approval".
 *   - A CLOSED PULL REQUEST STILL READ AS `proposed`, deliberately — a change is declined by
 *     a person and never inferred, so live resolution establishes closed-unmerged and then
 *     discards it. Right for the board; it left this gate with no way to see the closure.
 *   - RESOLUTION STOPS AFTER 25 ROWS fleet-wide, which is right for a board and useless
 *     here. The row a given workflow cared about was usually past the cap and came back
 *     unresolved, so a gate built on it was only ever fresh for the newest few workflows.
 *
 * SO THE QUESTION MOVED TO THE SERVER, where one row's pull request can actually be read:
 * GET /api/activities/:agent/:name/pending-proposal. It matches the workflow (on a WHOLE
 * token — `kb-parity-test` was being blocked by proposals against `kb-parity-test-b`), takes
 * the newest row, and asks GitHub about that one PR. The client's job shrinks to displaying
 * the answer, which is the part it can actually get right.
 */

/** How often the read repeats while the editor is open.
 *
 *  THIRTY SECONDS, against what it is racing: a person deciding a pull request in another
 *  tab. The rail polls its own state every five, but this is advisory — it only SUPPRESSES a
 *  button — and a read per five seconds per open editor would spend a GitHub status
 *  resolution (ADR-122 D3) on a question nobody asked. Half a minute is well inside the time
 *  it takes to approve something and come back. */
const AWAITING_REFRESH_MS = 30_000

export function useAwaitingProposal(agent: string, workflow: string): PendingProposal | null {
  const [awaiting, setAwaiting] = useState<PendingProposal | null>(null)

  useEffect(() => {
    if (!agent || !workflow) {
      setAwaiting(null)
      return
    }
    let live = true

    const read = () => {
      // ADVISORY, AND IT MUST NEVER BREAK AUTHORING.
      //
      // This read only suppresses a button. If it cannot be performed — the route is absent
      // on an older Mission Control, the network fails, a bundle is mismatched — the editor
      // must behave exactly as it did before it shipped, which is why every failure path
      // sets null rather than surfacing an error.
      //
      // NULL ON FAILURE MEANS THE GATE OPENS, and that is the right way round. The server is
      // conservative when IT cannot establish the answer (an unreadable PR comes back
      // pending), so the only thing reaching this path is the client being unable to ask at
      // all — and an operator who cannot even reach the question must not be held by it. The
      // whole defect this replaces was a gate that stayed shut on an answer nobody could
      // change; a duplicate pull request is recoverable in one click, a locked editor is not.
      //
      // The call is wrapped because a missing method throws SYNCHRONOUSLY, before any
      // promise exists, and an exception in an effect unmounts the whole facet: an operator
      // would lose the draft they were editing because a decorative banner could not load.
      try {
        void api
          .getPendingProposal(agent, workflow)
          .then((answer) => {
            if (!live) return
            // ONE FIELD DECIDES. The server already weighed the pull request's state, the
            // row's age and the name match; re-deriving any of that here is what produced
            // two surfaces disagreeing about one fact in the first place.
            setAwaiting(answer.pending ? answer : null)
          })
          .catch(() => {
            if (live) setAwaiting(null)
          })
      } catch {
        setAwaiting(null)
      }
    }

    read()
    const t = setInterval(read, AWAITING_REFRESH_MS)
    return () => {
      // AND IT STOPS. A read that outlives its component sets state on an unmounted tree and
      // keeps asking a question nobody is listening to.
      live = false
      clearInterval(t)
    }
  }, [agent, workflow])

  return awaiting
}

/**
 * SETTLED means a proposal is no longer waiting on anybody.
 *
 * The three states a change does not come back from: `merged` (it is in the branch),
 * `deployed` (it is running), `declined` (somebody said no). Everything else — `proposed`,
 * and crucially an UNKNOWN status — is unsettled.
 *
 * UNKNOWN IS UNSETTLED, deliberately, and it is the guard that matters. Reading an
 * unresolved status as settled would hide the approve link for a change still waiting on a
 * person, which is the same defect as the one being fixed and worse: the operator loses the
 * only route to a pull request instead of being shown a stale one.
 */
export function proposalIsSettled(status: string | null | undefined): boolean {
  return status === 'merged' || status === 'deployed' || status === 'declined'
}

/**
 * useImprovementStatus — what the improvements list says about ONE change, kept fresh.
 *
 * REPORTED BY THE FLEET OWNER, 2026-09-17: they approved PR #1184 in Mission Control and
 * deployed it, and the editor still read "Proposed — PR #1184 · Review & approve in Mission
 * Control →" over a run still badged CURRENT. "Once again need to stay sync'd and reflect if
 * anything it is merged and deployed."
 *
 * "ONCE AGAIN" is the right phrase: it is the fourth instance in one day of a fact read once
 * and reported as current — after the Results card, the empty run slot and the propose panel.
 * The data was always available (`improvement_id` is on the run AND on the list row, and the
 * improvements API resolves status against GitHub per ADR-122 D3). Nothing asked it.
 *
 * SHARES THIS MODULE, AND ITS INTERVAL, with useAwaitingProposal: both answer "what does the
 * improvements list say about this workflow right now", and two different refresh rates for
 * one question is how two surfaces come to disagree.
 */
export function useImprovementStatus(improvementID: string | null | undefined,
  prUrl?: string | null): string | null {
  const [status, setStatus] = useState<string | null>(null)

  useEffect(() => {
    const id = (improvementID ?? '').trim()
    const url = (prUrl ?? '').trim()
    if (!id && !url) {
      setStatus(null)
      return
    }
    let live = true
    const read = () => {
      // EVERY STATUS, not just `proposed`: the whole point is to see it become `merged` and
      // then `deployed`, and a status-filtered read would make the change vanish rather
      // than advance — which is indistinguishable from "declined" to a reader.
      try {
        void api
          .getImprovements()
          .then(({ improvements }) => {
            if (!live) return
            setStatus(improvements.find((i) => id ? i.id === id : i.pr_url === url)?.status ?? null)
          })
          .catch(() => { if (live) setStatus(null) })
      } catch {
        setStatus(null)
      }
    }
    read()
    const t = setInterval(read, AWAITING_REFRESH_MS)
    return () => {
      live = false
      clearInterval(t)
    }
  }, [improvementID, prUrl])

  return status
}
